import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useGanttCustomViews } from '../../../hooks/useGanttCustomViews';
import { useScheduleView } from '../../../hooks/useScheduleView';
import { sectorActivitiesService } from '../../../services/sectorActivities.service';
import { useGanttLoader } from '../../../hooks/useGanttLoader';
import { getSelectedSectorSelector } from '../../../redux/slices/projectSlice';
import {
  setCalendars,
  setGanttLoading,
  setInitialGanttState
} from '../../../redux/slices/ganttSlice';
import { useTableConfiguration } from '../../../hooks/useTableConfiguration';
import { columnCache } from '../../../services/columnCache';
import ganttHeightManager from '../helpers/ganttHeightManager';
import {
  getNextUIDAtLoad,
  clearMaxUIDCache
} from '../../ganttContainer/gantt/gantt.helper';
import * as ganttActions from '../../../redux/slices/ganttSlice';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import { ScheduleImpactRequestActions } from '#redux/actions/scheduleImpactRequestActions.js';
import { setScheduleImpactRequestItems } from '../../ganttContainer/gantt/utils/scheduleImpactRequestsUtils';
import { resetSectorCache } from '../../../utils/activityParsingUtils';
import { killStorage } from '../../../assets/js/gantt_storage';

const SCHEDULE_ACTIONS = {
  SET_GANTT_FULLY_LOADED: 'SET_GANTT_FULLY_LOADED',
  RESET_LOADING_STATE: 'RESET_LOADING_STATE'
};

const CHANGE_INTERVAL_MS = 50;
let shouldShowOpacity = 0;
const initialScheduleState = {
  scheduleGanttFullyLoaded: false,
  currentGanttInstance: null,
};

let isGanttInProcessOfLoad = false;
const scheduleReducer = (state, action) => {
  switch (action.type) {
    case SCHEDULE_ACTIONS.SET_GANTT_FULLY_LOADED: {
      const newStateLoaded = {
        ...state,
        scheduleGanttFullyLoaded: true,
        ganttInstance: action.ganttInstance,
        ganttId: action.ganttId,
        isANewProject: action.isANewProject
      };
      return newStateLoaded;
    }
    case SCHEDULE_ACTIONS.RESET_LOADING_STATE: {
      const newStateReset = {
        ...state,
        scheduleGanttFullyLoaded: false
      };
      return newStateReset;
    }
    default:
      return state;
  }
};

const ScheduleContext = createContext(null);

let isGanttLoading = false;

export const ScheduleProvider = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();
  const { loadScheduleView } = useScheduleView();
  const { initializeGantt } = useGanttLoader();
  const { loadAndApplyDefaultView } = useGanttCustomViews();
  const selectedSector = useSelector(getSelectedSectorSelector);
  const projectState = useSelector((state) => state.projectState);
  const ganttState = useSelector((state) => state.ganttState);
  const [scheduleData, dispatchSchedule] = useReducer(
    scheduleReducer,
    initialScheduleState
  );
  const { configureTableColumns } = useTableConfiguration();

  const updateUrlWithoutNavigation = (sectorId, projectId) => {
    const pathname = window.location.pathname;
    
    if (pathname.includes('/ganttchart')) {
      window.history.replaceState(null, '', `/gantt/${sectorId}/ganttchart`);
      return;
    }
    
    if (pathname.includes('/schedule/')) {
      const orgId = pathname.split('/')[1];
      window.history.replaceState(null, '', `/${orgId}/project/${projectId}/schedule/${sectorId}/`);
      return;
    }
  };

  useEffect(() => {
    if (!selectedSector || !projectState.projectSelected) return;
    updateUrlWithoutNavigation(selectedSector, projectState.projectSelected);
  }, [selectedSector, projectState.projectSelected]);

  const loadGanttData = useCallback(async () => {
    const urlPath = window.location.pathname;
    const sectorIdFromUrl = urlPath.match(/\/gantt\/(\d+)\/ganttchart$/)?.[1];
    const currentSectorId = selectedSector || sectorIdFromUrl;
    if (!currentSectorId || isGanttLoading) {
      return;
    }
    try {
      if(isGanttInProcessOfLoad) {
        return;
      }
      isGanttInProcessOfLoad = true
      isGanttLoading = true;

      if(scheduleData.ganttInstance) {
        scheduleData.ganttInstance.destructor();
      }
      window.initializingGantt = true;
      dispatchSchedule({ type: SCHEDULE_ACTIONS.RESET_LOADING_STATE });
      columnCache.invalidateCache();
      resetSectorCache();
      window.loader.show();
      const scheduleViewLoaded = await loadScheduleView();
      dispatch(setInitialGanttState());
      if (!scheduleViewLoaded) {
        throw new Error('Failed to load schedule view');
      }
      configureTableColumns();

      const result =
        await sectorActivitiesService.loadSectorData(currentSectorId);

      if (result && !result.activities?.length) {
        window.loader.hide();
        isGanttLoading = false;
        window.initializingGantt = false;
        isGanttInProcessOfLoad = false;
        history.replace('/masterplan');
        return;
      }

      if (result) {
        if (result.calendars) {
          dispatch(setCalendars(result.calendars));
        }
        if (!result.calendars?.length) {
          history.replace(
            `${window.location.pathname.split('ganttchart')[0]}calendars`
          );
          message.warning(t('need_calendar_gantt'), 5);
          return;
        }

        clearMaxUIDCache(currentSectorId);
        killStorage();

        const newGanttInstance = await initializeGantt({
          sector: result.sector,
          activities: result.activities,
          links: result.links,
          calendars: result.calendars,
          visualizationConfig: {},
          dispatch
        });

        const ganttId =
          result.sector.gantts[result.sector.gantts.length - 1].id;

        await loadAndApplyDefaultView(newGanttInstance, currentSectorId);

        const findLastUnique = ganttState?.lastUniqueCorrelativeIds.find(
          (el) => parseInt(el.sectorId) === parseInt(selectedSector)
        );

        if (findLastUnique === undefined) {
          const nextUIDAtLoad = getNextUIDAtLoad(result.activities);
          const newData = [
            ...ganttState.lastUniqueCorrelativeIds,
            {
              sectorId: selectedSector,
              lastUniqueCorrelativeId: parseInt(nextUIDAtLoad)
            }
          ];
          dispatch(ganttActions.setLastUniqueCorrelativeIds(newData));
        }

        const scheduleImpactRequestItems = result.scheduleImpactRequestItems;
        const res = setScheduleImpactRequestItems(scheduleImpactRequestItems);
        dispatch(
          ScheduleImpactRequestActions.setScheduleImpactRequestItems(res)
        );

        newGanttInstance.adjustGridWidth();
        const onAutoScheduleFinished = () => {
          columnCache.initialize(newGanttInstance);

          dispatchSchedule({
            type: SCHEDULE_ACTIONS.SET_GANTT_FULLY_LOADED,
            ganttInstance: newGanttInstance,
            ganttId,
            isANewProject: result.isANewProject
          });

          window.initializingGantt = null;
          newGanttInstance.refreshData();
          dispatch(setGanttLoading(false));

          window.loader.hide();
          window.removeEventListener(
            'autoScheduleFinished',
            onAutoScheduleFinished
          );

          window.__hasFinishedAutoSchedule = null;
          window.to_use_react_gantt.showDate(new Date());
          isGanttLoading = false;
          isGanttInProcessOfLoad = false;
        };

        if (window.__hasFinishedAutoSchedule) {
          onAutoScheduleFinished();
        } else {
          window.addEventListener(
            'autoScheduleFinished',
            onAutoScheduleFinished
          );
        }
      } else {
        window.loader.hide();
        isGanttLoading = false; // Release lock if no result
        isGanttInProcessOfLoad = false;
      }
    } catch (error) {
      isGanttLoading = false; // Release lock on error
      isGanttInProcessOfLoad = false;
      window.loader.hide();

      window.initializingGantt = false;

      dispatchSchedule({ type: SCHEDULE_ACTIONS.RESET_LOADING_STATE });
    }
  }, [selectedSector, window.location.pathname]);

  const isLegacyGanttUrl = (pathname) => {
    const ganttUrlRegex = /^\/gantt\/\d+\/ganttchart$/;
    return ganttUrlRegex.test(pathname);
  };

  const isNewGanttUrl = (pathname) => {
    const newGanttUrlRegex = /^\/(\d+)\/project\/(\d+)\/schedule\/(\d+)\/?$/;
    return newGanttUrlRegex.test(pathname);
  };

  /**
   * Handles routing logic for Gantt URLs:
   * - Validates if current sector matches URL sector
   * - Redirects to appropriate route if needed
   * - Returns true if should load Gantt data
   */
  const handleGanttRouting = (pathname) => {
    const urlMatch = pathname.match(/\/gantt\/(\d+)\/ganttchart$/);
    const urlSectorId = urlMatch ? parseInt(urlMatch[1]) : null;

    if (urlSectorId && selectedSector && urlSectorId !== selectedSector) {
      history.replace(`/gantt/${selectedSector}/ganttchart`);
      return false;
    }

    if (urlSectorId && !selectedSector) {
      history.replace('/masterplan');
      return false;
    }

    return true;
  };

  useEffect(() => {
    isGanttLoading = false;
    dispatch(ScheduleImpactRequestActions.setIsInPreview(false));
    dispatch(ScheduleImpactRequestActions.setIsModalOpen(false));
    dispatch(ScheduleImpactRequestActions.setTaskId(null));
    dispatch(ScheduleImpactRequestActions.setOriginModal(null));

    const shouldLoadGantt = (isLegacyGanttUrl(window.location.pathname) || 
                           isNewGanttUrl(window.location.pathname)) &&
                           handleGanttRouting(window.location.pathname);

    if (shouldLoadGantt) {
      shouldShowOpacity = 0;
      const debounceTimer = setTimeout(loadGanttData, CHANGE_INTERVAL_MS);
      return () => clearTimeout(debounceTimer);
    } else {
      shouldShowOpacity = 1;
      isGanttInProcessOfLoad = false;
    }
  }, [selectedSector, window.location.pathname]);

  useEffect(() => {
    if (scheduleData.scheduleGanttFullyLoaded) {
      ganttHeightManager.start();
      shouldShowOpacity = 1;
    }

    return () => {
      ganttHeightManager.stop();
    };
  }, [scheduleData.scheduleGanttFullyLoaded]);

  const isGanttChartRoute = isLegacyGanttUrl(window.location.pathname) || 
                           isNewGanttUrl(window.location.pathname);

  if (!isGanttChartRoute) {
    shouldShowOpacity = 1;
  }

  const containerStyle = {
    opacity: shouldShowOpacity,
    transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
    width: '100%',
    height: '100%'
  };

  return (
    <ScheduleContext.Provider
      value={{
        scheduleGanttFullyLoaded: scheduleData.scheduleGanttFullyLoaded,
        ganttInstance: scheduleData.ganttInstance,
        ganttId: scheduleData.ganttId,
        isANewProject: scheduleData.isANewProject
      }}>
      <div style={containerStyle}>{children}</div>
    </ScheduleContext.Provider>
  );
};

ScheduleProvider.propTypes = {
  children: PropTypes.node.isRequired
};

/**
 * @returns {boolean} true si el gantt está completamente cargado, false en caso contrario
 * @throws {Error} Si se usa fuera de ScheduleProvider
 */
export const useSchedule = () => {
  const context = useContext(ScheduleContext);
  if (context === null) {
    throw new Error('useSchedule must be used within a ScheduleProvider');
  }
  return context;
};

export default ScheduleContext;
